import { ConversationInclude } from '@/hooks/use-ai-conversations';
import { ConciergeSource } from '@/utils/constants';
import { CheckinTypeKey } from '@/utils/usercrypt/checkins';
import { WorkflowIds } from '@/utils/workflows';
import { EnumEventName, EnumGuidePageSource } from '@calm/evexia-analytics';
import {
    ConversationRequest,
    ConversationResponse,
    UsercryptCheckinEntrySummary,
    UsercryptChoice,
} from '@calm/evexia-web-sdk';

export interface SelectionResponseObj {
    input_text?: string;
    choiceIds?: string[];
}

export interface ConciergeState {
    /** True until the active circle's concierge permissions have settled. */
    isConciergeLoading: boolean;
    /** False while the permissions shown are held through a masked circle JWT; actions that need a live JWT wait for this. */
    isConciergePermissionsLive: boolean;
    /** True while a concierge step is still expected; consumers must not treat a missing step as terminal until it clears. */
    isResolvingStep: boolean;
    isConciergeGAEnabled: boolean;
    /** True until both the circle's concierge permissions and the GA flag have settled, i.e. until `isConciergeGAEnabled` is trustworthy. */
    isConciergeGALoading: boolean;
    canCreateConcierge: boolean;
    canDeleteConcierge: boolean;
    isNewPlan: boolean;
    /** Where the current flow was entered from, for the analytics `Source` prop. */
    conciergeSource: ConciergeSource | undefined;
    /** Which guide page button started the current flow, or undefined when it didn't start there. */
    guidePageSource: EnumGuidePageSource | undefined;
    /* Which check-in type the user chose in the guide menu (goal vs topic) */
    selectedCheckinType: CheckinTypeKey;
    setSelectedCheckinType: (checkinType: CheckinTypeKey) => void;
    usercryptData: UsercryptCheckinEntrySummary | undefined;
    setUsercryptData: (data: UsercryptCheckinEntrySummary | undefined) => void;
    selectionResponseData: SelectionResponseObj | undefined;
    setSelectionResponseData: (
        selectionResponse?: SelectionResponseObj
    ) => void;
    conversationData: ConversationResponse | undefined;
    setConversationData: (data: ConversationResponse | undefined) => void;
    refinementStepVersion: number;
    setRefinementStepVersion: (version: number) => void;
    retrieveRefinementMultipleChoiceOptions: (params: {
        parentChoiceId: string;
    }) => UsercryptChoice[] | undefined;
    retrieveRefinementHeadingData: (params: {
        choiceId: string;
    }) => string | undefined;
    saveAiConversation: (
        conversationRequest: ConversationRequest,
        include?: ConversationInclude
    ) => Promise<void>;
    isSaveConversationPending: boolean;
    saveAiRecommendation: (
        conversationId: string
    ) => Promise<string | undefined>;
    isSaveRecommendationPending: boolean;
    /** True when the last attempt to generate recommendations or create the Care Plan failed. */
    hasPlanGenerationFailed: boolean;
    clearPlanGenerationFailure: () => void;
    /** The Care Plan the last successful attempt created, until a consumer opens it. */
    generatedCollectionId: string | undefined;
    clearGeneratedCollectionId: () => void;
    isUpdatingSelection: boolean;
    setIsUpdatingSelection: (isUpdating: boolean) => void;
    markTypewriterTitleComplete: (title: string) => void;
    isTypewriterTitleComplete: (title: string) => boolean;
    clearTypewriterTitleCache: () => void;
    conversationId: string | undefined;
    isConversationIdLoading: boolean;
}

export const INITIAL_GOALS_SCREEN_VIEWED_EVENT =
    EnumEventName.ConciergeGoalStarted;

/*
 * Both concierge flows, because an instance is what it is regardless of what
 * the GA flag currently says: the flag reads false until it settles and can
 * flip between sessions, so filtering to the flag-derived id alone would hide a
 * GA user's own in-progress workflow.
 */
export const CONCIERGE_WORKFLOW_IDS: string[] = [
    WorkflowIds.CONCIERGE_FTUE,
    WorkflowIds.CONCIERGE_GA_FTUE,
];
